package fr.manyverse.app.infrastructure.monitoring

import fr.manyverse.app.AppLogger
import io.github.jan.supabase.logging.LogLevel
import io.github.jan.supabase.logging.SupabaseLoggingProcessor

/**
 * Redirige les logs internes de supabase-kt vers [AppLogger].
 *
 * Par défaut, supabase-kt écrit dans un Logger Kermit privé, branché uniquement sur la sortie
 * native : le writer Sentry ajouté par `initMonitoring()` ne les voyait jamais. Or ce sont les
 * seules traces de plusieurs échecs survenant hors de nos appels : refresh de session refusé,
 * session illisible au démarrage, channel Realtime rejeté par le serveur.
 *
 * Les logs d'erreur accompagnés d'une exception deviennent donc des erreurs Sentry, les autres
 * des breadcrumbs.
 */
internal class AppLoggerSupabaseProcessor(level: LogLevel) : SupabaseLoggingProcessor {

    // Jamais en dessous d'INFO : en DEBUG, supabase-kt logge le corps des join Realtime, qui
    // contient l'access token, et chaque log devient un breadcrumb Sentry
    private val minLevel = maxOf(level, LogLevel.INFO)

    override fun isEnabled(level: LogLevel): Boolean = level != LogLevel.NONE && level >= minLevel

    override fun processLog(level: LogLevel, tag: String, throwable: Throwable?, message: String) {
        val safeMessage = redactSecrets(message)
        when (level) {
            LogLevel.ERROR -> AppLogger.error(tag, safeMessage, throwable)
            // Seules les erreurs transportent l'exception : on garde sa nature dans le message
            LogLevel.WARNING -> AppLogger.warn(tag, safeMessage.withCause(throwable))
            LogLevel.INFO -> AppLogger.info(tag, safeMessage.withCause(throwable))
            LogLevel.DEBUG -> AppLogger.debug(tag, safeMessage.withCause(throwable))
            LogLevel.NONE -> Unit
        }
    }

    private fun String.withCause(throwable: Throwable?): String =
        if (throwable == null) {
            this
        } else {
            "$this (${throwable::class.simpleName}: ${redactSecrets(throwable.message.orEmpty())})"
        }
}

private val JWT = Regex("""eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+""")
private val SUPABASE_KEY = Regex("""sb_(publishable|secret)_[A-Za-z0-9_-]+""")
private val APIKEY_PARAM = Regex("""(apikey=)[^&\s]+""")

/**
 * Masque les secrets que supabase-kt peut glisser dans ses messages : access token en clair
 * quand la récupération de l'utilisateur échoue, clé d'API dans l'URL de la websocket Realtime.
 */
internal fun redactSecrets(message: String): String = message
    .replace(JWT, "[jwt]")
    .replace(SUPABASE_KEY, "[key]")
    .replace(APIKEY_PARAM, "$1[key]")
